import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";

import { fetchUserPermissions } from "../features/user/user-slice";
import { onTokenRefreshed, startSessionRefresh } from "./api-client";
import { StoreType } from "./types";

// Silent retries before a cold-load failure is shown to the user.
const MAX_AUTO_RETRIES = 1;

/**
 * Holds supervisor routes behind the loading screen until the user's
 * permissions are known, so a cold reload never flashes a menu item the user
 * cannot use nor bounces them off a page they can open. After a login the
 * response has already loaded them and this renders straight through.
 *
 * Also keeps them current: every session refresh refetches the set, so an
 * admin's role change reaches the user without a re-login.
 */
const PermissionsGate = () => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation();
	const { status, isLoaded } = useSelector(
		(state: StoreType) => state.user.permissionsLoad
	);
	const autoRetryCount = useRef(0);

	useEffect(() => {
		startSessionRefresh();
		const unsubscribe = onTokenRefreshed(() => {
			dispatch(fetchUserPermissions());
		});
		return unsubscribe;
	}, [dispatch]);

	useEffect(() => {
		if (isLoaded) return;
		if (status === "idle") {
			dispatch(fetchUserPermissions());
		} else if (
			status === "error" &&
			autoRetryCount.current < MAX_AUTO_RETRIES
		) {
			autoRetryCount.current += 1;
			dispatch(fetchUserPermissions());
		}
	}, [dispatch, isLoaded, status]);

	const handleRetry = useCallback(() => {
		dispatch(fetchUserPermissions());
	}, [dispatch]);

	if (isLoaded) {
		return <Outlet />;
	}

	const hasFailed =
		status === "error" && autoRetryCount.current >= MAX_AUTO_RETRIES;

	return (
		<Box
			height='100vh'
			width={1}
			display='flex'
			justifyContent='center'
			alignItems='center'
			flexDirection='column'
		>
			{hasFailed ? (
				<>
					<Typography role='alert'>
						{t("user_permissions-load-error")}
					</Typography>
					<Box mt={2}>
						<Button variant='contained' onClick={handleRetry}>
							{t("user_permissions-retry")}
						</Button>
					</Box>
				</>
			) : (
				<>
					<CircularProgress />
					<Box mt={1}>
						<Typography>{t("user_logging-in")}</Typography>
					</Box>
				</>
			)}
		</Box>
	);
};

export default PermissionsGate;
